<template>
  <div class="mobileFrameDisplay" :class="mobileDeviceType">
    <div class="mobileFrameInner">
      <RfCloseButtonPreview v-if="isInterstitial && closeButtonEnabled" :actions="actions" />
      <div :class="modalWrapperClasses" :style="modalBackgroundColor">
        <div ref="rfmodalbackground" class="mobilewebModalContentWrapper" :style="cssVars">
          <RfCloseButtonPreview v-if="closeButtonEnabled && !isInterstitial" :actions="actions" />
          <div v-if="isVideo" class="rfmodalvideo-backgroundimage relative">
            <div v-if="isVideo" class="video-volume-control">
              <span class="video-volume-up">
                <v-icon
                  v-if="actions.rf_settings_mobile_video_muted === 'true'"
                  size="24"
                  :style="{ color: fillColor }"
                  >md:volume_off</v-icon
                >
                <v-icon v-else size="24" class="video-volume-up" :style="{ color: fillColor }"
                  >md:volume_up</v-icon
                >
              </span>
            </div>
            <video
              ref="promoVideoPlayer"
              :key="backgroundVideoKey"
              crossorigin="anonymous"
              :autoplay="actions.rf_settings_mobile_video_autoplayed === 'true'"
              :loop="actions.rf_settings_mobile_video_loop === 'true'"
              :muted="actions.rf_settings_mobile_video_muted === 'true'"
              :poster="actions.rf_settings_mobile_video_poster"
              style="max-width: 100%"
              @ended="endedPlaying"
            >
              <source
                v-if="videoMediaType !== 'm3u8'"
                :src="backgroundVideo"
                :type="backgroundVideoType"
              />
              Your browser does not support the HTML5 Video element.
            </video>
            <v-icon
              v-if="actions.rf_settings_mobile_video_autoplayed !== 'true'"
              size="24"
              class="rfmodal-play"
              :style="{ color: fillColor }"
            >
              fa-play
            </v-icon>
            <img
              ref="promoVideoPoster"
              class="videoPosterImgDefault"
              :src="actions.rf_settings_bg_image"
            />
          </div>
          <div>
            <div>
              <!-- adding two layered divs to match brig ^^ -->
              <div class="rfmodal-header-mobileweb">
                <h3 :style="mobileWebTitleFontSize" v-html="parsedMobileTitle"></h3>
              </div>
              <div class="rfmodal-body-mobileweb">
                <RfBodyCountdown :actions="actions" />
                <div
                  :style="mobileWebBodyFontSize"
                  class="rfmodal-message-mobileweb"
                  v-html="mobileMessage"
                ></div>
              </div>
            </div>
          </div>
          <RfPromoBillingPreview v-if="hasBillingForm" :actions="actions" />
          <template v-else>
            <RfPromoInputsPreview :actions="actions" />
            <RfPromoSurveyPreview
              :actions="actions"
              :isMobile="true"
              :deviceType="model.device_type"
            />
          </template>
          <RfPromoConsentPreview
            v-if="consent.needsConsent.value"
            v-model="consent.consentChecked.value"
            :actions="actions"
          />
          <div>
            <!-- extra div to match brig ^^ -->
            <RfRetentionMessageButton
              :actions="actions"
              :model="model"
              :acceptButtonEnabled="consent.consentNotNeededOrGiven.value"
              mobileWeb
            />
          </div>
          <RfRetentionLinksPreview
            :fillColor="actions.rf_settings_fill_color"
            :text="actions?.rf_settings_privacy_policy_text"
          />
        </div>

        <div
          v-if="!isVideo"
          ref="rfmodal"
          :style="containerMobileWebBackgroundImage"
          class="rfmodal-backgroundimage"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import RfRetentionLinksPreview from "@/components/RfPathCreate/RfRetentionActions/components/RfRetentionLinksPreview.vue";
import RfRetentionMessageButton from "@/components/RfPathCreate/RfRetentionActions/components/RfRetentionMessageButton.vue";
import PromoPreviewMixin from "@/utils/PromoPreviewMixin";
import RfPromoInputsPreview from "@/components/RfPathCreate/RfRetentionActions/components/RfPromoInputsPreview.vue";
import RfCloseButtonPreview from "@/components/RfPathCreate/RfRetentionActions/RfCloseButtonPreview.vue";
import RfPromoBillingPreview from "@/components/RfPathCreate/RfRetentionActions/components/RfPromoBillingPreview.vue";
import RfPromoSurveyPreview from "@/components/RfPathCreate/RfRetentionActions/components/RfPromoSurveyPreview.vue";
import RfPromoConsentPreview from "@/components/RfPathCreate/RfRetentionActions/components/RfPromoConsentPreview.vue";
import Hls from "hls.js";
import { bottomLeft, bottomRight, topLeft, topRight } from "@/utils/constants/PromptStyleConstants";
import { baseUrlReplacer } from "@/utils/BaseUrlReplacer";
import { useAppsStore } from "@/pinia/appsStore";
import { useConsentPreview } from "@/utils/composables/useConsentPreview";
import RfBodyCountdown from "./RfBodyCountdown.vue";
import { useLiquidContextStore } from "@/pinia/liquidContextStore";

export default {
  name: "RfRetentionModalMobileContent",
  components: {
    RfRetentionLinksPreview,
    RfRetentionMessageButton,
    RfPromoInputsPreview,
    RfCloseButtonPreview,
    RfPromoBillingPreview,
    RfPromoSurveyPreview,
    RfPromoConsentPreview,
    RfBodyCountdown,
  },
  mixins: [PromoPreviewMixin],
  props: {
    actions: Object,
    model: Object,
    newButtonStyles: { type: Boolean, default: false }, // PromoPreviewMixin
  },
  setup: props => ({
    appsStore: useAppsStore(),
    consent: useConsentPreview(props),
    liquidContextStore: useLiquidContextStore(),
  }),
  computed: {
    hasBillingForm() {
      return `${this.actions.rf_billing_update_enabled}` === "true";
    },
    parsedMobileTitle() {
      return this.liquidContextStore.liquidify(this.actions?.rf_mobile_title);
    },
    isInterstitial() {
      return this.model.path_type === "interstitial";
    },
    backgroundVideoKey() {
      if (
        (this.actions.rf_settings_mobile_video_media_type &&
          this.actions.rf_settings_mobile_video_media_type.length === 0) ||
        this.actions.rf_settings_mobile_video_media_type === "m3u8" ||
        this.actions.rf_settings_video_media_type.length === 0 ||
        this.actions.rf_settings_video_media_type === "m3u8"
      ) {
        return "staticKey";
      }
      return this.backgroundVideo;
    },
    videoSrc() {
      return this.actions?.rf_settings_mobile_video_src || this.actions.rf_settings_video_src;
    },
    videoMediaType() {
      return (
        this.actions?.rf_settings_mobile_video_media_type ||
        this.actions.rf_settings_video_media_type
      );
    },
    isVideo() {
      return this.model.path_type === "video";
    },
    cssVars() {
      let bgColor;
      if (this.actions.button1_bg_color) {
        bgColor = this.actions.button1_bg_color;
      }
      if (this.isVideo) {
        return { "--btn-color": bgColor, "--txt-color": this.fillColor };
      }
      return {
        "padding-top": this.actions.rf_settings_mobile_title_padding_top,
        "--btn-color": bgColor,
        "--txt-color": this.fillColor,
      };
    },
    modalBackgroundColor() {
      return {
        "background-color": this.actions.rf_settings_bg_image_color,
        ...Object.fromEntries(
          [topRight, topLeft, bottomRight, bottomLeft].map(({ value }) => [
            `border-${value}-radius`,
            this.actions[`rf_mobile_border-${value}-radius`],
          ]),
        ),
        "overflow": "hidden",
        "border-width": this.actions["rf_mobile_border-width"],
        "border-style": this.actions["rf_mobile_border-style"],
        "border-color": this.actions["rf_mobile_border-color"],
        "box-sizing": this.actions["rf_mobile_box-sizing"],
        "box-shadow": this.actions["rf_mobile_box-shadow"],
      };
    },
    mobileWebTitleFontSize() {
      return {
        "font-size": this.actions.rf_settings_mobile_title_font_size,
      };
    },
    mobileWebBodyFontSize() {
      return {
        "font-size": this.actions.rf_settings_mobile_message_font_size,
      };
    },
    containerMobileWebBackgroundImage() {
      if (this.actions.rf_settings_mobile_bg_image) {
        return {
          "background-image": `url(${baseUrlReplacer(this.appsStore.app, this.liquidContextStore.liquidify(this.actions.rf_settings_mobile_bg_image))})`,
        };
      }
      if (this.actions.rf_settings_bg_image) {
        return {
          "background-image": `url(${baseUrlReplacer(this.appsStore.app, this.liquidContextStore.liquidify(this.actions.rf_settings_bg_image))})`,
        };
      }
      return {
        "background-image": "none",
      };
    },

    mobileDeviceType() {
      if (this.model.device_type === "android_os") {
        return "androidMobilePhone";
      }
      if (this.model.device_type === "ios") {
        return "iosMobilePhone appMocks";
      }
      if (this.model.device_type === "web") {
        return "iosMobilePhone";
      }
      return "";
    },
    backgroundVideo() {
      return this.actions.rf_settings_mobile_video_src || this.actions.rf_settings_video_src;
    },
    backgroundVideoType() {
      return `video/${this.actions.rf_settings_mobile_video_media_type || this.actions.rf_settings_video_media_type}`;
    },

    modalWrapperClasses() {
      const classes = ["mobileWebModalWrapper"];
      if (this.isVideo) classes.push("mobileWebVideo");
      return classes;
    },
  },
  watch: {
    videoSrc(to, from) {
      if (to && to.length && to !== from) {
        this.attachHlsVideo(to);
      }
    },
  },
  methods: {
    endedPlaying() {
      this.$refs.promoVideoPlayer.classList.add("fadeOutVideo");
      this.$refs.promoVideoPoster.classList.add("fadeInPoster");
      this.showPoster = true;
    },
    attachHlsVideo(url) {
      this.$nextTick(() => {
        const mediaType = url.split(".").pop();
        if (mediaType === "m3u8") {
          const video = this.$refs.promoVideoPlayer;
          if (Hls.isSupported()) {
            this.hls.detachMedia();
            this.hls = new Hls();
            this.hls.attachMedia(video);
            this.hls.on(Hls.Events.MEDIA_ATTACHED, () => {
              this.hls.loadSource(url);
            });
          } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
            video.src = url;
            video.addEventListener("loadedmetadata", () => {
              video.play();
            });
          }
        }
      });
    },
  },
};
</script>

<style scoped>
.rfmodal-play {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}
.video-volume-control {
  position: absolute;
  bottom: 20px;
  right: 20px;
}
</style>
